import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, TrendingUp, Shield, Clock, ArrowRight, Loader2 } from 'lucide-react';
import { useCMSContent } from '../hooks/useCMSContent';

export const OwnersPage = () => {
  // Fetch CMS content
  const { data: ownerBenefits, loading: benefitsLoading } = useCMSContent('ownerBenefits');
  const { data: companyInfo, loading: companyLoading } = useCMSContent('companyInfo');

  // Show loading state
  if (benefitsLoading && !ownerBenefits) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 className="animate-spin" size={48} color="var(--brand-primary)" />
      </div>
    );
  }

  const icons = [TrendingUp, Shield, CheckCircle, Clock];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'white' }} data-testid="owners-page">
      {/* Hero */}
      <section style={{
        position: 'relative',
        backgroundColor: 'var(--bg-section-light)',
        padding: '100px 0 80px',
        overflow: 'hidden'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <h1 className="heading-1" style={{ marginBottom: '24px' }}>
                For Property Owners
              </h1>
              <p className="body-large" style={{ marginBottom: '30px', color: 'var(--text-secondary)' }}>
                Professional property management that maximizes returns while minimizing your time commitment. We treat your investment like our own.
              </p>
              <Link to="/contact" className="btn-primary">
                Request Management Information
                <ArrowRight size={20} style={{ marginLeft: '8px' }} />
              </Link>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_3edf4311-b53d-4a6a-800b-8fbb025f103f/artifacts/e57divhp_LRMG%20Prop%201.jpg"
                alt="Happy Property Owners"
                style={{
                  width: '100%',
                  height: '400px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits - No Blue Boxes */}
      <section className="section-spacing">
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '50px', maxWidth: '800px', margin: '0 auto 50px' }}>
            <h2 className="heading-1" style={{ marginBottom: '16px' }}>
              Why Choose Laurel Run Management Group
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              We combine professional expertise with responsive service to deliver exceptional results.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }} data-testid="owner-benefits-grid">
            {(ownerBenefits || []).map((benefit, index) => {
              const Icon = icons[index % icons.length];
              return (
                <div
                  key={benefit.id || benefit.documentId || index}
                  style={{
                    backgroundColor: 'white',
                    padding: '32px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-light)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                  }}
                  className="card-hover"
                  data-testid={`owner-benefit-${benefit.id || index}`}
                >
                  <Icon size={36} color="var(--brand-primary)" strokeWidth={1.5} style={{ marginBottom: '20px' }} />
                  <h3 style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    color: 'var(--text-primary)',
                    marginBottom: '12px'
                  }}>
                    {benefit.title}
                  </h3>
                  <p style={{
                    color: 'var(--text-secondary)',
                    fontSize: '0.9375rem',
                    lineHeight: 1.7
                  }}>
                    {benefit.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Handle - Kentucky Photo */}
      <section className="section-spacing" style={{ backgroundColor: 'var(--bg-section-light)' }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
            gap: '60px',
            alignItems: 'center'
          }}>
            <div>
              <h2 className="heading-2" style={{ marginBottom: '28px' }}>
                We Handle Everything
              </h2>
              <p className="body-medium" style={{ marginBottom: '30px', color: 'var(--text-secondary)' }}>
                From day-to-day operations to long-term strategy, our comprehensive management approach covers all aspects of property ownership.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {[
                  { title: 'Marketing & Leasing', desc: 'Professional property marketing and tenant placement' },
                  { title: 'Tenant Screening', desc: 'Thorough background, credit, and reference checks' },
                  { title: 'Rent Collection', desc: 'Consistent collection with late payment enforcement' },
                  { title: 'Maintenance', desc: '24/7 emergency response and preventive maintenance' },
                  { title: 'Financial Reporting', desc: 'Detailed monthly statements and year-end summaries' },
                  { title: 'Legal Compliance', desc: 'Stay current with all regulations and requirements' }
                ].map((item, i) => (
                  <div key={i} style={{
                    padding: '20px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '1px solid var(--border-light)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                      <CheckCircle size={24} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <h4 style={{
                          color: 'var(--text-primary)',
                          fontWeight: 600,
                          fontSize: '1rem',
                          marginBottom: '6px'
                        }}>
                          {item.title}
                        </h4>
                        <p style={{
                          color: 'var(--text-secondary)',
                          fontSize: '0.875rem',
                          margin: 0
                        }}>
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <img
                src="https://images.unsplash.com/photo-1509826069158-41fafc8a4a42"
                alt="Professional Service"
                style={{
                  width: '100%',
                  height: '600px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid var(--border-light)'
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Financial Performance */}
      <section className="section-spacing">
        <div className="container">
          <div style={{
            backgroundColor: 'var(--bg-section-light)',
            padding: '60px',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <h2 className="heading-2" style={{ marginBottom: '24px' }}>
              Maximize Your Investment Returns
            </h2>
            <p className="body-large" style={{
              maxWidth: '800px',
              margin: '0 auto 40px',
              color: 'var(--text-secondary)'
            }}>
              Our strategic approach to property management focuses on protecting your asset value while optimizing rental income. Through market analysis, quality tenant placement, and proactive maintenance, we ensure your property performs at its best.
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '40px',
              marginTop: '50px'
            }}>
              {[
                { number: '95%+', label: 'Average Occupancy Rate' },
                { number: '24/7', label: 'Maintenance Response' },
                { number: '100%', label: 'Transparent Reporting' }
              ].map((stat, i) => (
                <div key={i}>
                  <div style={{
                    fontSize: 'clamp(2.5rem, 3rem + 0.5vw, 3.5rem)',
                    fontWeight: 700,
                    color: 'var(--brand-primary)',
                    fontFamily: 'Inter, sans-serif',
                    marginBottom: '10px'
                  }}>
                    {stat.number}
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: 'var(--text-secondary)',
                    fontWeight: 500
                  }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: 'linear-gradient(135deg, #1a3a52 0%, #2563eb 100%)',
        padding: '80px 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: 'clamp(2rem, 2.5rem + 0.5vw, 2.75rem)',
            color: 'white',
            marginBottom: '16px'
          }}>
            Ready to Work Together?
          </h2>
          <p style={{
            fontSize: '1.125rem',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '32px',
            maxWidth: '700px',
            margin: '0 auto 32px'
          }}>
            Contact us today to discuss your property management needs and learn how we can help maximize your investment.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary" style={{ backgroundColor: 'white', color: 'var(--brand-primary)' }}>
              Get Started Today
              <ArrowRight size={20} style={{ marginLeft: '8px' }} />
            </Link>
            <Link to="/services" className="btn-secondary" style={{ backgroundColor: 'transparent', borderColor: 'white', color: 'white', border: '2px solid white' }}>
              View Our Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
